import React from 'react';
import { Form } from '@ant-design/compatible';
import { InputNumber, Select, Spin, Switch } from 'antd';
import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from '../../utils/DataAccessUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import {
	formatRefrigerantTypeLabel,
	getApplianceCategoryOptions,
	REFRIGERANT_TYPE_PICKER,
} from '../../utils/RefrigerantUtils';

interface AssetTypeRefrigerantFormItemsProps {
	form: any;
	formData: any;
	/** The buyer's `refrigerantConfig.thresholdOverrides`, so the category labels show real thresholds. */
	thresholdOverrides: any;
	refrigerantTypes: any;
	fetchRefrigerantTypes: any;
	/**
	 * The parent form's single input width (`FORM_MAX_WIDTH`). Passed in rather than redeclared so this
	 * block cannot drift from the fields above it — the form previously carried three different widths
	 * and read as three unrelated forms stacked together.
	 */
	fieldStyle?: any;
}

/**
 * The refrigerant block of the ASSET TYPE form — the class-level defaults.
 *
 * Every field here is a default that an individual asset inherits and may override. A fleet of 200
 * identical roof-top units is configured once on the type; the one that was retrofitted last spring
 * carries its own values. The server still resolves asset-then-type
 * (`RefrigerantLeakRateUtil.effectiveRefrigerantFields`), but the asset form seeds its inputs from
 * these values and saves them onto the unit, so a correction made HERE reaches only the assets that
 * have not been saved since — the rest carry their own copy and have to be corrected themselves.
 *
 * Registers four fields on the parent's form: `usesRefrigerant`, `applianceCategory`,
 * `fullChargeAmount` and `refrigerantTypeCode`. `fullChargeAmount` is form-only and becomes the
 * `fullChargeLb` the server stores, mirroring the asset form's helper.
 *
 * `usesRefrigerant` is the asset TYPE's flag — "equipment of this class contains refrigerant" — and is
 * NOT the same field as the asset form's `isRefrigerantTracked`, which is per-unit enrolment in the
 * compliance programme. A 12 lb mini-split uses refrigerant and still sits under the scope floor, so
 * the two must stay separate. Nothing here required, unlike the asset form: a class-level default is
 * optional by definition, and refusing to save an asset type over a blank charge would block every
 * type that has no single answer.
 */
class AssetTypeRefrigerantFormItems extends React.Component<
	AssetTypeRefrigerantFormItemsProps,
	any
> {
	state = {
		// Mirrored into state rather than read back off the form on every render: the dependent fields
		// below are shown or hidden by the toggle, and a setState is what re-renders this subtree.
		usesRefrigerant: false,
	};

	componentDidMount() {
		const { formData, fetchRefrigerantTypes } = this.props;

		// no_pagination: the endpoint defaults to 10 rows and there are more refrigerants than that.
		fetchRefrigerantTypes({ no_pagination: true }, REFRIGERANT_TYPE_PICKER);

		this.setState({ usesRefrigerant: nullSafeGetOrElse('usesRefrigerant', formData, false) });
	}

	handleUsesRefrigerantChange = (usesRefrigerant) => this.setState({ usesRefrigerant });

	render() {
		const { form, formData, thresholdOverrides, refrigerantTypes, fieldStyle } = this.props;
		const { getFieldDecorator } = form;
		const { usesRefrigerant } = this.state;

		const refrigerantTypeRecords = getRecordsForTargetCollection(
			refrigerantTypes,
			REFRIGERANT_TYPE_PICKER
		);
		// Hidden with CSS rather than unmounted, matching the asset form: the registered values survive
		// a toggle-off-and-straight-back-on, and an unmounted field would submit as undefined and clear
		// the stored default (the asset-type PUT is a whole-row replace).
		const conditionalDisplay = { display: usesRefrigerant ? 'flex' : 'none' };

		return (
			<>
				<Form.Item label="Uses Refrigerant">
					{getFieldDecorator('usesRefrigerant', {
						initialValue: nullSafeGetOrElse('usesRefrigerant', formData, false),
						valuePropName: 'checked',
					})(<Switch onChange={this.handleUsesRefrigerantChange} />)}
				</Form.Item>
				<Form.Item label="EPA Appliance Category" style={conditionalDisplay}>
					{getFieldDecorator('applianceCategory', {
						initialValue: nullSafeGetWithZero('applianceCategory', formData),
					})(
						<Select style={fieldStyle} placeholder="Select appliance category" allowClear>
							{getApplianceCategoryOptions(thresholdOverrides).map((option) => (
								<Select.Option value={option.value} key={option.value}>
									{option.label}
								</Select.Option>
							))}
						</Select>
					)}
				</Form.Item>
				<Form.Item label="Full Refrigerant Charge Capacity" style={conditionalDisplay}>
					{getFieldDecorator('fullChargeAmount', {
						initialValue: nullSafeGetWithZero('fullChargeLb', formData),
					})(
						<InputNumber
							style={fieldStyle}
							min={0}
							// 5 dp is the column's precision (DECIMAL(14,5)); truncating here would silently
							// alter a regulated number for every asset inheriting it.
							precision={5}
							// Pounds is the only unit stored, so the unit is a fixed addon rather than a
							// picker — and it stays VISIBLE, because a bare box invites a kg entry that would
							// be read as lb.
							addonAfter="lb"
							placeholder="Full charge capacity"
						/>
					)}
				</Form.Item>
				<Form.Item label="Refrigerant type" style={conditionalDisplay}>
					{getFieldDecorator('refrigerantTypeCode', {
						initialValue: nullSafeGet('refrigerantTypeCode', formData),
					})(
						<Select
							style={fieldStyle}
							placeholder="Select refrigerant type"
							allowClear
							notFoundContent={
								refrigerantTypes.fetching ? <Spin size="small" /> : 'No refrigerant types'
							}
						>
							{refrigerantTypeRecords.map((refrigerantType) => (
								<Select.Option value={refrigerantType.code} key={refrigerantType.code}>
									{formatRefrigerantTypeLabel(refrigerantType)}
								</Select.Option>
							))}
						</Select>
					)}
				</Form.Item>
			</>
		);
	}
}

export default AssetTypeRefrigerantFormItems;
